/**
 * Tracking Module for CustomChatWidgetV2
 * Logs events to Supabase for dashboard analytics
 */

let supabase = null;
let currentSessionId = null;
let userEmail = null;
let userId = null;

// Initialize Supabase client (lazy-loaded)
async function getSupabaseClient() {
    if (supabase) return supabase;
    
    try {
        // Import dynamically to avoid requiring it in constants
        const supabaseModule = await import('../../supabase-client.ts');
        supabase = supabaseModule.supabase;
        return supabase;
    } catch (error) {
        console.warn('[Tracking] Supabase client not available:', error);
        return null;
    }
}

/**
 * Generate a UUID for session tracking
 */
export function createSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const randomValue = Math.floor(Math.random() * 16);
        const value = character === 'x' ? randomValue : (randomValue & 0x3) | 0x8;
        return value.toString(16);
    });
}

/**
 * Initialize tracking by fetching user info and creating session
 */
export async function initializeTracking() {
    try {
        const client = await getSupabaseClient();
        if (!client) return;
        
        // Get user from auth
        const { data: { user } } = await client.auth.getUser();
        if (user) {
            userId = user.id;
            userEmail = user.email;
        } else {
            // Fallback to localStorage for anonymous/temp users
            userId = localStorage.getItem('auth_id') || 'anonymous';
        }
        
        // Create new session ID
        if (!currentSessionId) {
            currentSessionId = createSessionId();
        }
        
        console.log('[Tracking] Initialized:', { userId, userEmail, sessionId: currentSessionId });
    } catch (error) {
        console.warn('[Tracking] Initialization error:', error);
    }
}

/**
 * Get current session ID (creates one if needed)
 */
export function getSessionId() {
    if (!currentSessionId) {
        currentSessionId = createSessionId();
    }
    return currentSessionId;
}

/**
 * Log a chat event to Supabase
 * Similar to v1's logChatEvent
 */
export async function logChatEvent({
    eventName,
    mode,
    agentType,
    custom = false,
    promptText,
    aiResponse,
    bookId = null,
    chapterNum = null,
}) {
    try {
        const client = await getSupabaseClient();
        if (!client || !eventName) return;
        
        const resolvedUserId = userId || localStorage.getItem('auth_id') || 'anonymous';
        if (resolvedUserId === 'anonymous' && !userId) {
            console.warn('[Tracking] Anonymous user, skipping event');
            return;
        }
        
        const sessionId = getSessionId();
        
        const payload = {
            user_id: resolvedUserId,
            event_name: eventName,
            book_id: bookId,
            book_title: null,
            chapter_num: chapterNum,
            stanza_num: null,
            content_type: 'chat',
            metadata: {
                session_id: sessionId,
                mode: mode || null,
                agent_type: agentType || null,
                custom: typeof custom === 'boolean' ? custom : null,
                prompt_text: promptText || null,
                ai_response: typeof aiResponse === 'string' ? aiResponse : null,
                user_email: userEmail || null,
            },
        };
        
        // Insert event into engagement_events table
        const { error } = await client
            .from('ENGAGEMENT_EVENTS')
            .insert([payload]);
        
        if (error) {
            console.warn('[Tracking] Failed to log event:', error);
        } else {
            console.log('[Tracking] Event logged:', eventName);
        }
    } catch (error) {
        console.warn('[Tracking] Error logging event:', error);
    }
}

export async function startChatInteraction({
    originalText,
    mode,
    agentType,
    isCustomAgent = false,
}) {
    const client = await getSupabaseClient();
    if (!client) return null;

    const resolvedUserId = userId || localStorage.getItem('auth_id') || 'anonymous';
    if (resolvedUserId === 'anonymous' && !userId) {
        return null;
    }

    const sessionId = getSessionId();
    const payload = {
        user_id: resolvedUserId,
        event_name: 'chat_prompt_submitted',
        book_id: null,
        book_title: null,
        chapter_num: null,
        stanza_num: null,
        content_type: 'chat',
        metadata: {
            session_id: sessionId,
            mode: mode || null,
            agent_type: agentType || null,
            custom: typeof isCustomAgent === 'boolean' ? isCustomAgent : null,
            prompt_text: originalText || null,
            ai_response: null,
            user_email: userEmail || null,
        },
    };

    const { data, error } = await client
        .from('ENGAGEMENT_EVENTS')
        .insert([payload])
        .select('id')
        .single();

    if (error) {
        console.warn('[Tracking] Failed to create chat interaction:', error);
        return null;
    }

    return data?.id || null;
}

export async function finalizeChatInteraction({ eventId, aiResponse, isError = false }) {
    if (!eventId) return;

    const client = await getSupabaseClient();
    if (!client) return;

    const { data: existingRow, error: fetchError } = await client
        .from('ENGAGEMENT_EVENTS')
        .select('metadata')
        .eq('id', eventId)
        .single();

    if (fetchError) {
        console.warn('[Tracking] Failed to load chat interaction before finalizing:', fetchError);
        return;
    }

    const mergedMetadata = {
        ...(existingRow?.metadata || {}),
        ai_response: typeof aiResponse === 'string' ? aiResponse : null,
        interaction_status: isError ? 'error' : 'success',
    };

    const { error } = await client
        .from('ENGAGEMENT_EVENTS')
        .update({
            metadata: mergedMetadata,
        })
        .eq('id', eventId);

    if (error) {
        console.warn('[Tracking] Failed to finalize chat interaction:', error);
    }
}

/**
 * Log widget open/close event
 */
export async function logWidgetToggle(isOpen) {
    await logChatEvent({
        eventName: isOpen ? 'chat_widget_opened' : 'chat_widget_closed',
        mode: null,
        agentType: null,
        custom: false,
    });
}

/**
 * Log mode selection event
 */
export async function logModeSelection(mode) {
    await logChatEvent({
        eventName: 'chat_mode_selected',
        mode: mode,
        agentType: null,
        custom: false,
    });
    
    // Also track with Mixpanel if available
    if (window.mixpanel) {
        window.mixpanel.track('Chat Mode Selected', { mode });
    }
}

/**
 * Log user message submission with agent info
 */
export async function logMessageSubmitted({
    originalText,
    mode,
    agentType,
    isCustomAgent = false,
}) {
    const interactionId = await startChatInteraction({
        originalText,
        mode,
        agentType,
        isCustomAgent,
    });
    
    // Also track with Mixpanel if available
    if (window.mixpanel) {
        window.mixpanel.track('Chat Message Submitted', {
            mode,
            agent_type: agentType,
            is_custom: isCustomAgent,
            prompt_text: originalText,
        });
    }

    return interactionId;
}

/**
 * Log language toggle
 */
export async function logLanguageToggle(language) {
    await logChatEvent({
        eventName: 'chat_language_toggled',
        mode: null,
        agentType: null,
        custom: false,
        promptText: `Changed to ${language}`,
    });
}

/**
 * Log session reset
 */
export async function logSessionReset() {
    await logChatEvent({
        eventName: 'chat_session_reset',
        mode: null,
        agentType: null,
        custom: false,
    });
    
    // Create new session on reset
    currentSessionId = createSessionId();
}

export function getCurrentUserId() {
    return userId || localStorage.getItem('auth_id') || 'anonymous';
}

export function getCurrentUserEmail() {
    return userEmail;
}
